import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { safeStringify } from '@shared/safeStringify';
import { JsonSyntax } from './JsonSyntax';
import { RowsResponseSchema, type CorrelatedRow, type RowsResponse } from './types';
import { readStoredJson } from '../../../lib/readStoredJson';

const ROWS_PAGE_LIMIT = 50;

// ── Badges ────────────────────────────────────────────────────

const LegacyBadge = () => (
  <span className="ml-1 text-[10px] rounded px-1 py-0.5 bg-bg-tertiary text-icon-subtle">legacy</span>
);
const V4Badge = () => (
  <span className="ml-1 text-[10px] rounded px-1 py-0.5 bg-blueberry-50 text-blueberry-400">v4</span>
);

// ── Column configuration ─────────────────────────────────────

export type ColId = string;

interface ColDef {
  id: ColId;
  label: string;
  source: 'legacy' | 'v4';
  render: (row: CorrelatedRow) => React.ReactNode;
}

const ALL_COLUMNS: ColDef[] = [
  { id: 'legacyId', label: 'Legacy ID', source: 'legacy', render: (r) => <span className="font-mono text-xs text-content-tertiary flex items-center gap-1">{r.legacyId} <span className="text-icon-subtle">▸</span></span> },
  { id: 'name', label: 'Name', source: 'legacy', render: (r) => <span className="truncate max-w-[200px] block" title={r.artifactName ?? undefined}>{r.artifactName ?? '—'}</span> },
  { id: 'account', label: 'Account', source: 'legacy', render: (r) => <span className="truncate max-w-[150px] block" title={r.accountName ?? undefined}>{r.accountName ?? '—'}</span> },
  { id: 'category', label: 'Category', source: 'legacy', render: (r) => r.category ?? '—' },
  { id: 'status', label: 'Status', source: 'legacy', render: (r) => r.status ?? '—' },
  { id: 'owner', label: 'Owner', source: 'legacy', render: (r) => <span className="truncate max-w-[180px] block" title={r.ownerEmail ?? undefined}>{r.ownerEmail ?? '—'}</span> },
  { id: 'timestamp', label: 'Timestamp', source: 'legacy', render: (r) => <span className="text-xs text-icon-subtle">{r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}</span> },
  { id: 'summary', label: 'Summary', source: 'legacy', render: (r) => <span className="truncate max-w-[200px] block" title={r.summary ?? undefined}>{r.summary ?? '—'}</span> },
  { id: 'externalId', label: 'External ID', source: 'legacy', render: (r) => <span className="font-mono text-xs truncate max-w-[160px] block" title={r.externalId ?? undefined}>{r.externalId ?? '—'}</span> },
  { id: 'v4Kind', label: 'Kind', source: 'v4', render: (r) => r.v4 ? <span className="inline-flex items-center rounded-full bg-blueberry-50 px-2 py-0.5 text-xs font-medium text-blueberry-700">{r.v4.kind}</span> : '—' },
  { id: 'v4Created', label: 'Created', source: 'v4', render: (r) => <span className="text-xs text-icon-subtle">{r.v4 ? new Date(r.v4.createdAt).toLocaleString() : '—'}</span> },
  { id: 'v4Uuid', label: 'UUID', source: 'v4', render: (r) => <span className="font-mono text-xs truncate max-w-[120px] block" title={r.v4?.uuid}>{r.v4?.uuid?.slice(0, 8) ?? '—'}</span> },
  { id: 'v4ExternalId', label: 'External ID', source: 'v4', render: (r) => <span className="font-mono text-xs truncate max-w-[160px] block" title={r.v4?.externalId}>{r.v4?.externalId ?? '—'}</span> },
  { id: 'v4Read', label: 'Read', source: 'v4', render: (r) => r.v4 ? (r.v4.read ? <span className="text-content-success">✓</span> : <span className="text-icon-subtle">✗</span>) : '—' },
  { id: 'v4Blueprint', label: 'Blueprint', source: 'v4', render: (r) => { const displayName = r.v4?.blueprint?.displayName; return displayName ? <span className="text-xs">{safeStringify(displayName)}</span> : '—'; } },
  { id: 'v4SfObjectId', label: 'SF Object ID', source: 'v4', render: (r) => { const sfId = r.v4?.salesforceObjectId ?? null; return sfId ? <span className="font-mono text-xs truncate max-w-[120px] block" title={sfId}>{sfId.slice(0, 12)}</span> : '—'; } },
  { id: 'bridged', label: 'Bridged', source: 'v4', render: (r) => r.v4 ? <span className="inline-block h-2 w-2 rounded-full bg-matcha-500" title="Bridged" /> : <span className="inline-block h-2 w-2 rounded-full bg-nightshade-300" title="Not bridged" /> },
];

const DEFAULT_COLS: ColId[] = ['legacyId', 'name', 'account', 'category', 'status', 'owner', 'timestamp', 'v4Kind', 'bridged'];

const StoredColsSchema = z.array(z.string()).min(1);

export function readStoredCols(): ColId[] {
  return readStoredJson(localStorage, 'dual-write-columns', StoredColsSchema, [...DEFAULT_COLS]);
}

// ── Panel definitions ────────────────────────────────────────

type PanelId = 'legacy' | 'payload' | 'artifact' | 'blueprint' | 'request';

const PANEL_DEFS: Array<{ id: PanelId; label: string; badge: string; badgeClass: string; borderClass: string }> = [
  { id: 'legacy', label: 'Legacy Row', badge: 'legacy', badgeClass: 'bg-bg-tertiary text-content-tertiary', borderClass: 'border-border-secondary' },
  { id: 'payload', label: 'Wire Payload', badge: 'incoming', badgeClass: 'bg-tangerine-100 text-tangerine-700', borderClass: 'border-tangerine-200' },
  { id: 'artifact', label: 'V4 Artifact', badge: 'v4', badgeClass: 'bg-blueberry-100 text-blueberry-600', borderClass: 'border-blueberry-200' },
  { id: 'blueprint', label: 'Blueprint', badge: 'config', badgeClass: 'bg-ube-100 text-ube-600', borderClass: 'border-ube-200' },
  { id: 'request', label: 'HTTP Request', badge: 'http', badgeClass: 'bg-bg-green-light text-content-success', borderClass: 'border-border-green-light' },
];

const DEFAULT_PANELS: PanelId[] = ['legacy', 'payload', 'artifact'];

function RowDetailPanel({ row }: { row: CorrelatedRow }) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [activePanels, setActivePanels] = useState<PanelId[]>(DEFAULT_PANELS);
  const [replayState, setReplayState] = useState<{ loading: boolean; result?: string; ok?: boolean }>({ loading: false });

  const toggle = (id: PanelId) => {
    const next = activePanels.includes(id)
      ? (activePanels.length > 1 ? activePanels.filter((p: PanelId) => p !== id) : activePanels)
      : [...activePanels, id];
    setActivePanels(next);
  };

  const panelData: Record<PanelId, unknown> = {
    legacy: {
      id: row.legacyId,
      external_id: row.externalId,
      artifact_name: row.artifactName,
      account_name: row.accountName,
      category: row.category,
      approval_status: row.status,
      timestamp: row.timestamp,
      summary: row.summary,
      content: row.content,
      envelope: row.envelope,
    },
    payload: row.httpLog?.body ?? row.rawRequestBody ?? null,
    artifact: row.v4
      ? {
          uuid: row.v4.uuid,
          kind: row.v4.kind,
          externalId: row.v4.externalId,
          createdAt: row.v4.createdAt,
          read: row.v4.read,
          salesforceObjectId: row.v4.salesforceObjectId,
          ...(row.v4.data ? { data: row.v4.data } : {}),
        }
      : null,
    blueprint: row.v4?.blueprint ?? null,
    request: row.httpLog ? {
      method: row.httpLog.method,
      path: row.httpLog.path,
      statusCode: row.httpLog.statusCode,
      durationMs: row.httpLog.durationMs,
      headers: row.httpLog.headers,
      error: row.httpLog.error,
      timestamp: row.httpLog.timestamp,
    } : null,
  };

  return (
    <td colSpan={10} className="px-3 py-3 bg-bg-secondary/50">
      {/* Toggle pills */}
      <div className="flex items-center gap-1.5 mb-3">
        {PANEL_DEFS.map(def => {
          const isActive = activePanels.includes(def.id);
          return (
            <button
              key={def.id}
              type="button"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); toggle(def.id); }}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                isActive
                  ? 'bg-bg-raised border border-border-primary text-content-primary shadow-sm'
                  : 'bg-bg-tertiary border border-transparent text-icon-subtle hover:text-content-tertiary'
              }`}
            >
              {def.label}
              <span className={`text-[10px] px-1 py-0 rounded-full ${isActive ? def.badgeClass : 'bg-bg-tertiary text-icon-subtle'}`}>
                {def.badge}
              </span>
            </button>
          );
        })}
        {/* Replay + Copy buttons */}
        <div className="ml-auto flex items-center gap-2">
          {row.httpLog && (
            <>
              <button
                type="button"
                disabled={replayState.loading}
                className="inline-flex items-center gap-1 text-xs text-icon-subtle hover:text-content-tertiary border border-border-secondary rounded-md px-2 py-1 transition-colors disabled:opacity-50"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (!window.confirm('This will re-ingest this payload. Continue?')) return;
                  void (async () => {
                    setReplayState({ loading: true });
                    try {
                      const res = await apiFetch(`/api/admin/http-logs/${row.httpLog!.id}/replay`, { method: 'POST' });
                      const status = res.status;
                      setReplayState({ loading: false, result: `Replayed → ${status}`, ok: res.ok });
                      void queryClient.invalidateQueries({ queryKey: ['admin', 'dual-write'] });
                    } catch (err) {
                      setReplayState({ loading: false, result: err instanceof Error ? err.message : 'Replay failed', ok: false });
                    }
                  })();
                }}
              >
                {replayState.loading ? 'Replaying...' : 'Replay'}
              </button>
              {replayState.result && (
                <span className={`text-xs font-medium ${replayState.ok ? 'text-content-success' : 'text-content-danger'}`}>
                  {replayState.result}
                </span>
              )}
            </>
          )}
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-icon-subtle hover:text-content-tertiary transition-colors"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              const copyData: Record<string, unknown> = {};
              for (const id of activePanels) copyData[id] = panelData[id];
              void navigator.clipboard.writeText(JSON.stringify(copyData, null, 2));
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
              <path d="M5.5 3.5A1.5 1.5 0 0 1 7 2h2.879a1.5 1.5 0 0 1 1.06.44l2.122 2.12a1.5 1.5 0 0 1 .439 1.061V9.5A1.5 1.5 0 0 1 12 11V8.621a3 3 0 0 0-.879-2.121L9 4.379A3 3 0 0 0 6.879 3.5H5.5Z" />
              <path d="M4 5a1.5 1.5 0 0 0-1.5 1.5v6A1.5 1.5 0 0 0 4 14h5a1.5 1.5 0 0 0 1.5-1.5V8.621a1.5 1.5 0 0 0-.44-1.06L7.94 5.439A1.5 1.5 0 0 0 6.878 5H4Z" />
            </svg>
            Copy
          </button>
        </div>
      </div>
      {/* Panels */}
      <div className="flex gap-3">
        {activePanels.map(id => {
          const _def = PANEL_DEFS.find(d => d.id === id)!;
          const data = panelData[id];
          return (
            <div key={id} className="flex-1 min-w-0">
              {data != null ? (
                <JsonSyntax data={data} />
              ) : (
                <div className="text-xs text-icon-subtle bg-nightshade-900/50 rounded-lg p-3 font-mono">
                  {id === 'payload' ? 'No payload' : id === 'artifact' ? 'Not bridged' : id === 'blueprint' ? 'No blueprint' : id === 'request' ? 'No HTTP log' : 'Empty'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </td>
  );
}

// ── Column Config Dropdown ───────────────────────────────────

function ColumnConfigDropdown({ columns, onChange }: { columns: ColId[]; onChange: (cols: ColId[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (ref.current && !ref.current.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleCol = (id: ColId) => {
    if (columns.includes(id)) {
      if (columns.length > 1) onChange(columns.filter((c: ColId) => c !== id));
    } else {
      onChange([...columns, id]);
    }
  };

  const moveCol = (id: ColId, dir: -1 | 1) => {
    const idx = columns.indexOf(id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= columns.length) return;
    const next = [...columns];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  };

  const legacyCols = ALL_COLUMNS.filter((c: typeof ALL_COLUMNS[number]) => c.source === 'legacy');
  const v4Cols = ALL_COLUMNS.filter((c: typeof ALL_COLUMNS[number]) => c.source === 'v4');

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-content-tertiary bg-bg-raised border border-border-secondary hover:border-border-primary hover:bg-bg-secondary transition-colors"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7m0-18H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7m0-18v18" /><path d="M7 8h0M7 12h0M7 16h0" /></svg>
        Columns <span className="text-icon-subtle">({columns.length})</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-72 bg-bg-raised rounded-lg border border-border-secondary shadow-lg py-1 max-h-[400px] overflow-auto">
          {/* Active columns (reorderable) */}
          {columns.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-icon-subtle uppercase tracking-wider">Active ({columns.length})</div>
              {columns.map((id, idx) => {
                const def = ALL_COLUMNS.find(c => c.id === id);
                if (!def) return null;
                return (
                  <div key={id} className="flex items-center gap-1 px-3 py-1 hover:bg-bg-secondary group">
                    <button type="button" className="text-icon-subtle hover:text-content-tertiary text-xs disabled:opacity-20" disabled={idx === 0} onClick={(e: React.MouseEvent) => { e.stopPropagation(); moveCol(id, -1); }}>▲</button>
                    <button type="button" className="text-icon-subtle hover:text-content-tertiary text-xs disabled:opacity-20" disabled={idx === columns.length - 1} onClick={(e: React.MouseEvent) => { e.stopPropagation(); moveCol(id, 1); }}>▼</button>
                    <label className="flex items-center gap-2 flex-1 cursor-pointer text-xs" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      <input type="checkbox" checked className="rounded border-border-primary text-blueberry-600 h-3 w-3" onChange={() => toggleCol(id)} />
                      <span className="text-content-primary">{def.label}</span>
                      <span className={`ml-auto text-[10px] px-1 rounded-full ${def.source === 'v4' ? 'bg-blueberry-50 text-blueberry-600' : 'bg-bg-tertiary text-icon-subtle'}`}>{def.source}</span>
                    </label>
                  </div>
                );
              })}
              <div className="border-t border-border-tertiary my-1" />
            </>
          )}
          {/* Legacy columns */}
          <div className="px-3 py-1.5 text-[10px] font-semibold text-icon-subtle uppercase tracking-wider">Legacy Fields</div>
          {legacyCols.filter(c => !columns.includes(c.id)).map(col => (
            <label key={col.id} className="flex items-center gap-2 px-3 py-1 hover:bg-bg-secondary cursor-pointer text-xs" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <input type="checkbox" checked={false} className="rounded border-border-primary text-blueberry-600 h-3 w-3" onChange={() => toggleCol(col.id)} />
              <span className="text-icon-subtle">{col.label}</span>
              <span className="ml-auto text-[10px] px-1 rounded-full bg-bg-tertiary text-icon-subtle">legacy</span>
            </label>
          ))}
          {/* V4 columns */}
          <div className="px-3 py-1.5 text-[10px] font-semibold text-icon-subtle uppercase tracking-wider mt-1">V4 Fields</div>
          {v4Cols.filter(c => !columns.includes(c.id)).map(col => (
            <label key={col.id} className="flex items-center gap-2 px-3 py-1 hover:bg-bg-secondary cursor-pointer text-xs" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <input type="checkbox" checked={false} className="rounded border-border-primary text-blueberry-600 h-3 w-3" onChange={() => toggleCol(col.id)} />
              <span className="text-icon-subtle">{col.label}</span>
              <span className="ml-auto text-[10px] px-1 rounded-full bg-blueberry-50 text-blueberry-600">v4</span>
            </label>
          ))}
          {/* Reset */}
          <div className="border-t border-border-tertiary mt-1 pt-1 pb-1">
            <button type="button" className="w-full text-left px-3 py-1 text-xs text-icon-subtle hover:text-content-tertiary" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onChange([...DEFAULT_COLS]); }}>
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rows Table ───────────────────────────────────────────────

export function RowsTable({ filter }: { filter?: 'bridged' | 'unbridged' }) {
  const { apiFetch } = useApi();
  const [offset, setOffset] = useState(0);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [visibleCols, setVisibleCols] = useState<ColId[]>(readStoredCols());
  const handleColChange = (cols: ColId[]) => {
    setVisibleCols(cols);
    try { localStorage.setItem('dual-write-columns', JSON.stringify(cols)); } catch {}
  };

  // Reset pagination and expanded row when filter changes
  useEffect(() => { setOffset(0); setExpandedRow(null); }, [filter]);

  const { data, isLoading, error } = useQuery<RowsResponse>({
    queryKey: ['admin', 'dual-write', 'rows', offset, filter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(ROWS_PAGE_LIMIT), offset: String(offset) });
      if (filter) params.set('filter', filter);
      return fetchJson(apiFetch, RowsResponseSchema, `/api/v4/admin/dual-write/rows?${params}`);
    },
  });

  if (isLoading) {
    return <div className="text-icon-subtle py-8 text-center text-sm">Loading rows...</div>;
  }

  if (error) {
    return <div className="text-content-danger py-4 text-sm">Failed to load rows.</div>;
  }

  const rows = data?.rows ?? [];

  const activeDefs = visibleCols
    .map((id) => ALL_COLUMNS.find((c) => c.id === id))
    .filter((c): c is ColDef => c !== undefined);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-content-tertiary">Recent Rows</span>
        <ColumnConfigDropdown columns={visibleCols} onChange={handleColChange} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-border-secondary">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary text-left text-xs text-icon-subtle uppercase">
            <tr>
              {activeDefs.map(col => (
                <th key={col.id} className="px-3 py-2">
                  {col.label}
                  {col.source === 'v4' ? <V4Badge /> : <LegacyBadge />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-tertiary">
            {rows.map((row) => {
              const isExpanded = expandedRow === row.legacyId;
              return (
                <React.Fragment key={row.legacyId}>
                  <tr
                    className={`cursor-pointer ${isExpanded ? 'bg-blueberry-50/30' : 'hover:bg-bg-secondary'}`}
                    onClick={() => setExpandedRow(isExpanded ? null : row.legacyId)}
                  >
                    {activeDefs.map(col => (
                      <td key={col.id} className="px-3 py-2">{col.render(row)}</td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr>
                      <RowDetailPanel row={row} />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <button
          type="button"
          className="text-sm text-content-tertiary hover:text-content-primary disabled:text-icon-subtle disabled:cursor-not-allowed"
          onClick={() => setOffset(Math.max(0, offset - ROWS_PAGE_LIMIT))}
          disabled={offset === 0}
        >
          Previous
        </button>
        <span className="text-xs text-icon-subtle">
          Showing {offset + 1}–{offset + rows.length}
        </span>
        <button
          type="button"
          className="text-sm text-content-tertiary hover:text-content-primary disabled:text-icon-subtle disabled:cursor-not-allowed"
          onClick={() => setOffset(offset + ROWS_PAGE_LIMIT)}
          disabled={!data?.hasMore}
        >
          Next
        </button>
      </div>
    </div>
  );
}
